/**
 * Prompts — Extraction des champs métier.
 *
 * Pass 2+ : pour chaque pass (primes, garanties, assurés), on demande à Claude
 * d'extraire les valeurs structurées avec citations textuelles obligatoires.
 */

export const EXTRACTION_PROMPT_VERSION = "v3.2.0";

export const EXTRACTION_SYSTEM_PROMPT = `Tu es un assistant expert en extraction de données depuis des PDFs d'assurance collective.

RÈGLES STRICTES :
1. Réponse en JSON strict, jamais de texte libre.
2. Pour CHAQUE champ extrait, fournir :
   - \`value\` : la valeur typée (number pour montants, string sinon)
   - \`unit\` : '$', '%', 'mois', 'salaire', etc.
   - \`confidence\` : 0..1 (1 = mot-à-mot dans le PDF)
   - \`citation_quote\` : extrait textuel EXACT du PDF (max 200 caractères)
   - \`citation_page\` : numéro de page où la valeur a été trouvée (1-indexé)
3. Si un champ n'est PAS dans le document, mets-le à null. NE JAMAIS INVENTER.
4. Si une valeur apparaît multiple fois, prends la plus précise (ex: dans un tableau de tarifs vs dans le sommaire).
5. Pour les montants : pas de séparateur de milliers, virgule décimale autorisée → convertir en number.
6. Si le PDF est en anglais, extrais quand même les valeurs et conserve le \`citation_quote\` dans la langue d'origine.

CHAMPS DEMANDÉS DANS CE PASS (voir liste dans le user prompt).`;

/**
 * Pass IDs supportés. Chaque pass cible un sous-ensemble de champs.
 * Permet de paralléliser et de re-tenter un pass échoué isolément.
 */
export const EXTRACTION_PASSES = {
  identification: {
    id: "identification",
    label: "Identification du document",
    fields: ["insurer_code", "document_type", "language", "effect_date"],
  },
  primes: {
    id: "primes",
    label: "Primes mensuelles et annuelles",
    fields: ["prime_mensuelle_total", "prime_annuelle_total", "prime_par_garantie"],
  },
  vie_dma: {
    id: "vie_dma",
    label: "Vie de base + DMA",
    fields: [
      "vie_base.salary_multiple",
      "vie_base.max_amount",
      "vie_base.reduction_age",
      "vie_base.elimination",
      "dma.coverage",
      "dma.max_amount",
    ],
  },
  amc: {
    id: "amc",
    label: "Assurance Maladie Complémentaire",
    fields: [
      "amc.deductible",
      "amc.coinsurance",
      "amc.max_annual",
      "amc.medicaments_coinsurance",
      "amc.paramedical_max",
      "amc.hospital_room",
      "amc.travel_max",
    ],
  },
  dentaire: {
    id: "dentaire",
    label: "Soins dentaires",
    fields: [
      "dentaire.preventif_coinsurance",
      "dentaire.basic_coinsurance",
      "dentaire.major_coinsurance",
      "dentaire.ortho_coinsurance",
      "dentaire.max_annual",
      "dentaire.fee_guide_year",
    ],
  },
  invalidite: {
    id: "invalidite",
    label: "Invalidité court terme et long terme",
    fields: [
      "ic_court_terme.percentage",
      "ic_court_terme.max_weekly",
      "ic_court_terme.elimination",
      "ic_court_terme.duration",
      "ic_long_terme.percentage",
      "ic_long_terme.max_monthly",
      "ic_long_terme.elimination",
      "ic_long_terme.definition",
    ],
  },
  conditions: {
    id: "conditions",
    label: "Conditions générales (admissibilité, exclusions)",
    fields: ["admissibility_hours", "waiting_period_days", "exclusions", "general_notes"],
  },
} as const;

export type ExtractionPassId = keyof typeof EXTRACTION_PASSES;

/**
 * Passes **MÉTIER** — toutes les passes sauf `identification`.
 *
 * Dérivé de `EXTRACTION_PASSES`, jamais recopié : ajouter une passe au contrat la
 * rend automatiquement métier, donc automatiquement soumise au gate humain-only
 * `extraction.business_pass` (chunk 9.AA.0). Une liste en dur laisserait une
 * nouvelle passe franchir le gate permissif `extractions.run` en silence.
 *
 * `identification` est délibérément exclue : elle ne lit que la structure du
 * document (assureur, langue, sections présentes), elle est déjà exposée par
 * l'outil MCP `run_extraction` et utilisée par le parcours agent existant. La
 * décision PO 9.AA.1 §3 vise les passes métier, pas celle-là.
 */
export const BUSINESS_EXTRACTION_PASS_IDS = (
  Object.keys(EXTRACTION_PASSES) as ExtractionPassId[]
).filter((id) => id !== "identification");

/** Vrai si `passId` désigne une passe métier (donc humain-only). */
export function isBusinessExtractionPass(passId: string | undefined): boolean {
  return passId !== undefined && (BUSINESS_EXTRACTION_PASS_IDS as string[]).includes(passId);
}

/** Action_key du gate humain-only des passes métier (seedé migration 0064). */
export const BUSINESS_PASS_GATING_ACTION_KEY = "extraction.business_pass";

export function buildExtractionUserPrompt(
  passId: ExtractionPassId,
  extractedText: string,
  fileName: string,
): string {
  const pass = EXTRACTION_PASSES[passId];
  const fieldList = pass.fields.map((f) => `  - ${f}`).join("\n");

  return `Pass : ${pass.label}
Fichier : ${fileName}

Champs à extraire :
${fieldList}

Réponds en JSON strict :
{
  "fields": {
    "<field_key>": {
      "value": ...,
      "unit": "...",
      "confidence": 0.0,
      "citation_quote": "...",
      "citation_page": 1
    }
  }
}

Texte extrait du PDF :
---
${extractedText}
---`;
}
